/**
 * Shared hook for fetching template variant summary data.
 * Extracted from ItemVariantsSummaryWidget for reuse in toggle/builder guards.
 */

import { useQuery } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// --- Types ---

export interface VariantSummaryTemplate {
  item_code: string;
  item_name: string;
  image: string | null;
}

export interface VariantSummaryItem {
  item_code: string;
  item_name: string;
  image: string | null;
  attributes: Record<string, string>;
  price_list_rate: number | null;
}

export interface VariantSummaryResponse {
  template: VariantSummaryTemplate;
  attribute_options: Record<string, string[]>;
  total_variants: number;
  variants: VariantSummaryItem[];
}

// --- Hook ---

export function useTemplateVariantsSummary(itemCode: string, enabled: boolean) {
  return useQuery<VariantSummaryResponse>({
    queryKey: ["template-variants-summary", itemCode],
    queryFn: () =>
      frappeMethod<VariantSummaryResponse>(
        "zaviago_backend.api.pos_variant_api.get_template_variants_summary",
        { template_item_code: itemCode, limit: 200 },
      ),
    enabled: !!itemCode && enabled,
    staleTime: 5 * 60 * 1000,
    // No retries — if the item isn't saved as a template yet, retrying won't help.
    // The error fallback in ItemVariantsSummaryWidget shows the toggle+builder. (OPS-1528)
    retry: false,
  });
}
